import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useOrderModal } from '../contexts/OrderModalContext';
import './MenuTransitions.css';

const MenuContent = (props) => {
    const [categories, setCategories] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentView, setCurrentView] = useState('categories');
    const [fadeClass, setFadeClass] = useState('fade-enter-active');
    const { openModal } = useOrderModal();
    
    const navigate = useNavigate();
    const { categorySlug } = useParams() || {};
    
    // Set initial view based on URL
    useEffect(() => {
        setCurrentView(categorySlug || 'categories');
    }, [categorySlug]);
    
    // Find current category if we're showing one
    const isShowingCategory = currentView !== 'categories';
    const currentCategory = isShowingCategory ? 
        categories.find(cat => cat.websiteUrl === `/menu/${currentView}`) : 
        null;

    // MAIN SCROLL EFFECT - handles all scrolling when categorySlug changes
    useEffect(() => {
        if (categorySlug) {
            // Scroll to top immediately
            window.scrollTo(0, 0);
            
            // Backup scroll after a brief delay
            setTimeout(() => {
                window.scrollTo(0, 0);
            }, 100);
        }
    }, [categorySlug]);

    // Separate function to fetch menu items
    const fetchMenuItemsForCategory = async (categorySlug) => {
        try {
            setError(null);
            
            const response = await fetch(`/api/menu-items/category/${categorySlug}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.success) {
                const sortedItems = data.data.sort((a, b) => (a.orderBy || 0) - (b.orderBy || 0));
                setMenuItems(sortedItems);
            } else {
                throw new Error(data.message || 'Failed to fetch menu items');
            }
        } catch (err) {
            console.error('Error fetching menu items:', err);
            setError(err.message);
        }
    };

    // Handle category clicks - FIXED to include data fetching
    const handleCategoryClick = (e, category) => {
        e.preventDefault();
        const categorySlug = category.websiteUrl.replace('/menu/', '');
        
        // Navigate to the category
        navigate(category.websiteUrl, { replace: true });
        
        // The useEffect above will handle scrolling when categorySlug changes
        // No need to manually scroll here since the useEffect will trigger
    };

    const handleOrderClick = (e, item) => {
        e.preventDefault();
        openModal();
    };

    // Fetch menu categories on component mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoading(true);
                
                const response = await fetch('/api/menu-categories/');
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const data = await response.json();
                
                if (data.success) {
                    setCategories(data.data);
                } else {
                    throw new Error(data.message || 'Failed to fetch categories');
                }
            } catch (err) {
                console.error('Error fetching menu categories:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    // Fetch menu items when a category is selected
    useEffect(() => {
        if (isShowingCategory && categories.length > 0) {
            fetchMenuItemsForCategory(currentView);
        } else if (!isShowingCategory) {
            setMenuItems([]);
        }
    }, [currentView, isShowingCategory, categories]);

    // Handle loading state
    if (loading) {
        return (
            <>
                <div className="columns">
                    <div className={`column is-flex is-flex-direction-column ${props.classNameTitle} is-justify-content-center`}>
                        <h1 className="h1 colorBlack">
                            {currentCategory ? currentCategory.category : props.title}
                        </h1>
                        <p>
                            {currentCategory ? 
                                currentCategory.description : 
                                <>
                                    There is something for every appetite, from our signature hot sandwiches to deli classics, fresh salads, savory sides, and smoked meats favorites. Offerings and prices may vary by location.<br /><Link to="/daily-specials">View our daily specials</Link>
                                </>
                            }
                        </p>
                    </div>
                </div>
                <div className="columns">
                    <div className={`column is-flex is-flex-direction-column ${props.className} is-justify-content-center`}>
                        <div className="has-text-centered">
                            <p>...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Handle error state
    if (error) {
        return (
            <>
                <div className="columns">
                    <div className={`column is-flex is-flex-direction-column ${props.classNameTitle} is-justify-content-center`}>
                        <h1 className="h1 colorBlack">
                            {currentCategory ? currentCategory.category : props.title}
                        </h1>
                        <p>
                            {currentCategory ? 
                                currentCategory.description : 
                                <>
                                    There is something for every appetite, from our signature hot sandwiches to deli classics, fresh salads, savory sides, and smoked meats favorites. Offerings and prices may vary by location.<br /><Link to="/daily-specials">View our daily specials</Link>
                                </>
                            }
                        </p>
                    </div>
                </div>
                <div className="columns">
                    <div className={`column is-flex is-flex-direction-column ${props.className} is-justify-content-center`}>
                        <div className="has-text-centered">
                            <p className="has-text-danger">Error: {error}</p>
                            <button 
                                className="button is-primary mt-3"
                                onClick={() => window.location.reload()}
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="columns">
                <div className={`column is-flex is-flex-direction-column ${props.classNameTitle} is-justify-content-center`}>
                    <h1 className="h1 colorBlack">
                        {currentCategory ? currentCategory.category : props.title}
                    </h1>
                    <p>
                        {currentCategory ? 
                            currentCategory.description : 
                            <>
                                There is something for every appetite, from our signature hot sandwiches to deli classics, fresh salads, savory sides, and smoked meats favorites. Menu items are subject to change.
                            </>
                        }
                    </p>
                    <div className="pt-4 m-0 px-0">
                        <Link to="/daily-specials">View Today's Daily Special</Link>
                    </div>
                </div>
            </div>

            {/* Category Navigation */}
            <div className="columns p-0 m-0">
                <div className={`column is-flex is-flex-direction-column ${props.className} is-justify-content-center`}>
                    {categories.length > 0 && (
                        <nav 
                            className="menu-categories-nav"
                            role="navigation"
                            aria-label="Menu categories navigation"
                        >
                            {/* Desktop Button Navigation */}
                            <div className="is-hidden-mobile is-flex is-flex-wrap-wrap is-justify-content-center is-align-items-center">
                                {categories.map((category) => {
                                    const categorySlugFromUrl = category.websiteUrl.replace('/menu/', '');
                                    const isActive = currentView === categorySlugFromUrl;
                                    
                                    return (
                                        <React.Fragment key={category._id}>
                                            <button
                                                onClick={(e) => handleCategoryClick(e, category)}
                                                className={`menu-category-link px-4 py-2 mx-2 my-1 is-size-5-desktop is-size-6-mobile ${isActive ? 'category-nav-active' : ''}`}
                                            >
                                                {category.category}
                                            </button>
                                        </React.Fragment>
                                    );
                                })}
                            </div>

                            {/* Mobile Dropdown Navigation */}
                            <div className="m-0 p-0 is-hidden-tablet">
                                <div className="m-0 p-0 field">
                                    <div className="m-0 p-0 control">
                                        <div className="m-0 p-0 location-selector-container">
                                            <select
                                                value={isShowingCategory ? currentView : 'categories'}
                                                onChange={(e) => {
                                                    const selectedValue = e.target.value;
                                                    if (selectedValue === 'categories') {
                                                        navigate('/menu', { replace: true });
                                                    } else {
                                                        const selectedCategory = categories.find(cat => 
                                                            cat.websiteUrl.replace('/menu/', '') === selectedValue
                                                        );
                                                        if (selectedCategory) {
                                                            handleCategoryClick(e, selectedCategory);
                                                        }
                                                    }
                                                }}
                                                className="location-selector"
                                            >
                                                <option value="categories">All Categories</option>
                                                {categories.map((category) => {
                                                    const categorySlugFromUrl = category.websiteUrl.replace('/menu/', '');
                                                    return (
                                                        <option 
                                                            key={category._id} 
                                                            value={categorySlugFromUrl}
                                                        >
                                                            {category.category}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </nav>
                    )}
                </div>
            </div>
            
            <div className="m-0 p-0 columns">
                <div className={`column column ${props.className}`}>
                    <div className={`menu-content-container ${fadeClass}`}>
                        {isShowingCategory ? (
                            // Show Menu Items for Selected Category
                            <div className="m-0 p-0 columns is-multiline">
                                {menuItems.length > 0 ? (
                                    menuItems.map((item) => {
                                        const primaryImage = item.photoSrc?.photos?.[0] || 
                                            (item.photos && item.photos.length > 0 ? item.photos[0] : null) ||
                                            (item.photoSrc && Array.isArray(item.photoSrc) && item.photoSrc[0]) ||
                                            item.image || null;
                                        
                                        return (
                                            <div key={item._id} className="my-5 p-0 column is-full borderRadius">
                                                <div 
                                                    className="m-0 p-0 is-flex is-flex-direction-column-mobile card-menu card-content-menu backgroundColorWhite borderRadius" 
                                                    style={{ minHeight: '298px' }}
                                                >
                                                    {/* Image Section */}
                                                    <div 
                                                        className="m-0 p-0 card-image-container"
                                                        style={{ 
                                                            position: 'relative',
                                                            width: '100%',
                                                            minHeight: '298px'
                                                        }}
                                                    >
                                                        {primaryImage ? (
                                                            <img 
                                                                src={primaryImage}
                                                                alt={item.name}
                                                                className="p-0 m-0 menu-itemName-image borderRadius"
                                                                style={{ 
                                                                    width: '100%', 
                                                                    height: '100%',
                                                                    minHeight: '298px', 
                                                                    objectFit: 'cover',
                                                                    objectPosition: 'center'
                                                                }}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling.style.display = 'flex';
                                                                }}
                                                            />
                                                        ) : null}
                                                        
                                                        <div className="m-0 p-0 borderRadius"
                                                            style={{ 
                                                                position: primaryImage ? 'absolute' : 'static',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%', 
                                                                height: '100%',
                                                                minHeight: '298px',  
                                                                background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
                                                                display: primaryImage ? 'none' : 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: '#999',
                                                                fontSize: '1rem',
                                                                textAlign: 'center',
                                                                padding: '1rem',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        >
                                                            <span>Missing Image - {item.name}</span>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Content Section */}
                                                    <div 
                                                        className="m-0 p-0 borderRadius menu-item-content-section"
                                                        style={{
                                                            flex: '1 1 auto',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            width: '100%'
                                                        }}
                                                    >
                                                        <div className="card-text m-4 is-align-left insideCard is-flex is-flex-direction-column is-justify-content-space-between" style={{ height: '100%' }}>
                                                            <div>
                                                                <h3 className="mx-4 mt-6 mb-2 subtitle is-4">
                                                                    {item.name}
                                                                </h3>
                                                                <p className="mx-4 menuDescription is-6">
                                                                    {item.description}
                                                                </p>
                                                            </div>

                                                            <div className="mb-4 mx-4 is-flex is-justify-content-end is-align-content-end">
                                                                <button
                                                                    onClick={(e) => handleOrderClick(e, item)}
                                                                    className="button"
                                                                    style={{
                                                                        backgroundColor: '#8B1E1E',
                                                                        borderColor: '#8B1E1E',
                                                                        color: 'white',
                                                                        transition: 'all 0.3s ease',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                    onMouseEnter={(e) => {
                                                                        e.target.style.backgroundColor = '#6B1515';
                                                                        e.target.style.borderColor = '#6B1515';
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        e.target.style.backgroundColor = '#8B1E1E';
                                                                        e.target.style.borderColor = '#8B1E1E';
                                                                    }}
                                                                >
                                                                    Order
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : null}
                            </div>
                        ) : (
                            // Show All Categories
                            <div className="columns is-multiline mt-1">
                                {categories.length > 0 ? (
                                    categories.map((category) => {
                                        const primaryImage = category.photoSrc.photos[0] || 
                                            (category.photos && category.photos.length > 0 ? category.photos[0] : null) ||
                                            (category.photoSrc && Array.isArray(category.photoSrc) && category.photoSrc[0]) ||
                                            null;
                                        return (
                                            <div key={category._id} className="column is-half-desktop is-full-mobile is-full-tablet">
                                                <div className="is-flex is-flex-direction-column card-menu backgroundColorWhite">
                                                    <div className="card-image borderRadius">
                                                        {primaryImage ? (
                                                            <figure className="image is-fullwidth borderRadius" style={{ height: '100%', margin: 0 }}>
                                                                <img 
                                                                    src={primaryImage} 
                                                                    alt={`${category.category} menu items`}
                                                                    style={{ 
                                                                        width: '100%', 
                                                                        height: '100%', 
                                                                        objectFit: 'cover',
                                                                        objectPosition: 'center'
                                                                    }}
                                                                    className="borderRadius"
                                                                />
                                                            </figure>
                                                        ) : (
                                                            <div 
                                                                style={{ 
                                                                    width: '100%', 
                                                                    height: '100%', 
                                                                    background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: '#999',
                                                                    fontSize: '1.2rem',
                                                                    textAlign: 'center',
                                                                    padding: '1rem'
                                                                }}
                                                            >
                                                                {category.category}
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    <div className="borderRadius" style={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', padding: '1.5rem' }}>
                                                        <div className="is-align-left" style={{ flex: '1 1 auto' }}>
                                                            <h3 className="m-2 pt-2 pb-0 subtitle">
                                                                {category.category}
                                                            </h3>
                                                            <p className="m-2 pt-0 pb-2">
                                                                {category.description}
                                                            </p>
                                                        </div>
                                                        
                                                        <div className="pt-4 is-flex is-justify-content-flex-end is-align-items-flex-end">
                                                            <button
                                                                onClick={(e) => handleCategoryClick(e, category)}
                                                                className="button"
                                                                style={{
                                                                    transition: 'all 0.3s ease',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                {category.label || 'View Items'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="has-text-centered">
                                        <p>No menu categories available at this time.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default MenuContent;